import { useMemo } from "react";
import { Pencil, Trash2 } from "lucide-react";
import {
  Field,
  StandardFieldDatabaseType,
  RelationshipFieldDatabaseType,
  FileFieldDatabaseType,
  UserFieldDatabaseType,
  SecretFieldDatabaseType,
} from "@/types/breeze/Field";
import { Badge, Box, Button, Flex, Text } from "@radix-ui/themes";
import { rectSortingStrategy } from "@dnd-kit/sortable";
import Surface from "@/components/ui/Surface";
import { IEntity } from "@/types/breeze/Entity";
import { SortableList, SortableItem, DragHandle } from "@/components/dnd";

interface DraggableFieldListProps {
  entity: IEntity;
  fields: Field[];
  onReorder: (reorderedFields: Field[]) => void;
  onEdit: (field: Field) => void;
  onDelete: (field: Field) => void;
}

const getFieldDescription = (field: Field) => {
  const isGeo =
    field.database_type === StandardFieldDatabaseType.JSON &&
    field.display_type === "geo";
  const typeLabel = isGeo ? "geo" : field.database_type;
  const parts: string[] = [typeLabel];
  if (field.is_required) parts.push("Required");
  if (field.is_unique) parts.push("Unique");
  if (field.is_name_field) parts.push("Name Field");
  return parts.join(" • ");
};

const FIELD_ICON_STYLE = {
  width: "16px",
  height: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "12px",
  fontWeight: "500",
} as const;

const FIELD_ICONS: Record<string, string> = {
  [StandardFieldDatabaseType.VARCHAR]: "Aa",
  [StandardFieldDatabaseType.TEXT]: "Aa",
  [StandardFieldDatabaseType.INTEGER]: "#",
  [StandardFieldDatabaseType.BIGINTEGER]: "#",
  [StandardFieldDatabaseType.DECIMAL]: "#",
  [StandardFieldDatabaseType.BOOLEAN]: "✓",
  [StandardFieldDatabaseType.DATE]: "📅",
  [StandardFieldDatabaseType.TIMESTAMP]: "📅",
  [StandardFieldDatabaseType.JSON]: "{}",
  [StandardFieldDatabaseType.GEO]: "📍",
  [RelationshipFieldDatabaseType.ONE_TO_ONE]: "1:1",
  [RelationshipFieldDatabaseType.ONE_TO_MANY]: "1:N",
  [RelationshipFieldDatabaseType.MANY_TO_ONE]: "N:1",
  [RelationshipFieldDatabaseType.MANY_TO_MANY]: "N:N",
  [RelationshipFieldDatabaseType.DYNAMIC_REFERENCE]: "🔗",
  [StandardFieldDatabaseType.RELATIONSHIP]: "↔️",
  [FileFieldDatabaseType.FILE]: "📎",
  [UserFieldDatabaseType.USER]: "👤",
  [SecretFieldDatabaseType.SECRET]: "🔒",
};

const FieldIcon = ({ field }: { field: Field }) => {
  const isGeo =
    field.database_type === StandardFieldDatabaseType.JSON &&
    field.display_type === "geo";
  const iconKey = isGeo ? StandardFieldDatabaseType.GEO : field.database_type;
  
  return (
    <Box
      style={{
        backgroundColor: "var(--gray-a4)",
        padding: "var(--space-2)",
        borderRadius: "var(--radius-2)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "42px",
        height: "42px",
        marginTop: "2px",
        marginRight: "8px",
      }}
    >
      <Box style={FIELD_ICON_STYLE}>{FIELD_ICONS[iconKey] ?? "?"}</Box>
    </Box>
  );
};

const FieldSurface = ({
  field,
  disableEdit,
  onEdit,
  onDelete,
  dragHandleProps,
}: {
  field: Field;
  disableEdit: boolean;
  onEdit: (field: Field) => void;
  onDelete: (field: Field) => void;
  dragHandleProps?: React.HTMLAttributes<HTMLElement>;
}) => (
  <Surface
    preTitleContent={
      <>
        {dragHandleProps && <DragHandle {...dragHandleProps} disabled={field.locked || field.is_system} />}
        <FieldIcon field={field} />
      </>
    }
    title={field.name}
    body={getFieldDescription(field)}
    selected={false}
    onClick={() => !disableEdit && !field.is_system && onEdit(field)}
    additionalTitleContent={
      <>
        <Flex gap="2" mr="4">
          {field.is_system && <Badge color="gray" variant="soft">System</Badge>}
          {field.is_required && <Badge>R</Badge>}
          {field.is_unique && <Badge>U</Badge>}
          {field.is_searchable && <Badge>S</Badge>}
          {field.publicly_searchable && <Badge>PS</Badge>}
          {field.is_name_field && <Badge>NF</Badge>}
        </Flex>
        <Button
          variant="ghost"
          color="gray"
          onClick={() => onEdit(field)}
          disabled={disableEdit || field.locked || field.is_system}
        >
          <Pencil style={{ width: "1rem", height: "1rem" }} />
        </Button>
      </>
    }
    actions={[
      {
        label: "Delete",
        icon: <Trash2 style={{ width: "1rem", height: "1rem", color: "var(--red-9)" }} />,
        onClick: () => onDelete(field),
        disabled: field.locked || disableEdit || field.is_system,
      },
    ]}
  />
);

export const DraggableFieldList = ({
  entity,
  fields,
  onReorder,
  onEdit,
  onDelete,
}: DraggableFieldListProps) => {
  const normalizedFields = useMemo(() => {
    const sorted = [...fields].sort((a, b) => a.sort_order - b.sort_order);
    return sorted.map((field, index) => ({ ...field, sort_order: index + 1 }));
  }, [fields]);

  if (normalizedFields.length === 0) {
    return (
      <Box
        style={{
          padding: "var(--space-8)",
          textAlign: "center",
          border: "2px dashed var(--gray-6)",
          borderRadius: "var(--radius-3)",
          backgroundColor: "var(--gray-a1)",
        }}
      >
        <Text size="3" color="gray">
          No fields yet. Create your first field to get started.
        </Text>
      </Box>
    );
  }

  const handleReorder = (reordered: Field[]) => {
    onReorder(reordered.map((f, i) => ({ ...f, sort_order: i + 1 })));
  };

  return (
    <SortableList
      items={normalizedFields}
      getItemId={(f) => f.name}
      onReorder={handleReorder}
      strategy={rectSortingStrategy}
      containerStyle={{ flexDirection: "row", flexWrap: "wrap" }}
      renderItem={(field) => (
        <SortableItem
          key={field.name}
          id={field.name}
          disabled={field.locked || field.is_system}
          style={{ width: field.width === 50 ? "calc(50% - 2px)" : "100%" }}
        >
          {({ dragHandleProps }) => (
            <FieldSurface
              field={field}
              disableEdit={entity.locked}
              onEdit={onEdit}
              onDelete={onDelete}
              dragHandleProps={dragHandleProps}
            />
          )}
        </SortableItem>
      )}
      renderDragOverlay={(field) => (
        <FieldSurface
          field={field}
          disableEdit={entity.locked}
          onEdit={onEdit}
          onDelete={onDelete}
          dragHandleProps={{}}
        />
      )}
    />
  );
};
